import React, { useMemo, useEffect, useContext } from 'react'
import usePrevious from 'hooks/usePrevious'
import { useDispatch } from 'react-redux'
import { useIdRegister } from 'hooks/useIdRegister'
import { useCardProvider } from '../KanBoProvider'
import useChannel from 'hooks/useChannel'
import useSaga from 'hooks/useSaga'
import { takeEvery } from 'redux-saga/effects'
import { CHANNEL_DATA } from 'CHANNEL_DATA'
import cardChannelData from './cardChannelData'
import fetchView from 'routes/Card/sagas/fetchView'

function * channelData (handler, args) {
  yield takeEvery(CHANNEL_DATA, handler, args)
}

const CardChannels: React.FC<{ cardId: number; }> = ({ cardId }) => {
  useChannel(`card-${cardId}`)
  useChannel(`activity-card-${cardId}`)

  const args = useMemo(() => ({
    cardId,
  }), [cardId])

  useSaga(channelData, cardChannelData, args)

  return null
}

const DEFAULT_CONTEXT: {
  registerCardView: (userId: number) => void;
  unregisterCardView: (userId: number) => void;
} = {
  registerCardView: () => {},
  unregisterCardView: () => {},
}

export const CardViewProviderContext = React.createContext(DEFAULT_CONTEXT)

export function CardViewProvider ({
  children
}) {
  const dispatch = useDispatch()

  const [ids, registerId, unregisterId] = useIdRegister<number>(5)
  const prevIds = usePrevious(ids)

  useEffect(() => {
    let addedIds: number[] = []

    if (ids.length) {
      if (prevIds?.length) {
        addedIds = ids.filter(boardId => !prevIds.includes(boardId))
      } else {
        addedIds = ids
      }
    }

    if (addedIds.length) {
      dispatch(addedIds.map(cardId => fetchView({
        cardId,
      })))
    }
  }, [ids])

  const value = useMemo(() => ({
    registerCardView: registerId,
    unregisterCardView: unregisterId,
  }), [registerId, unregisterId])

  return (
    <CardViewProviderContext.Provider value={value}>
      {ids.map(cardId =>
        <CardChannels
          key={cardId}
          cardId={cardId}
        />
      )}
      {children}
    </CardViewProviderContext.Provider>
  )
}

export function useCardViewProvider (cardId: number) {
  const { registerCardView, unregisterCardView } = useContext(CardViewProviderContext)

  const cardIds = useMemo(() => [cardId], [cardId])

  useCardProvider(cardIds)

  useEffect(() => {
    registerCardView(cardId)

    return () => {
      unregisterCardView(cardId)
    }
  }, [cardId])
}
